import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, ChevronRight, Printer } from 'lucide-react';
import { MarketSectionView } from '../../components/market/MarketBlocks';
import { MarketNotice, OpenMarketButton } from '../../components/market/MarketNotice';
import { MarketCalculators } from '../../components/market/MarketCalculators';
import { MarketWidgetView } from '../../components/market/MarketWidgets';
import { TopicChecklist } from '../../components/market/TopicChecklist';
import { printWithDetails } from '../../components/market/MarketShell';
import { CopyLinkButton } from '../../components/guide/CopyLinkButton';
import { marketTopics, topicByKey, topicBySlug, topicPath } from '../../data/market/topics';
import { MARKET_BASE_PATH, MARKET_SOURCE_LABEL } from '../../data/market/config';
import { useMarketProgress } from '../../hooks/useMarketProgress';
export function MarketTopicPage() {
  const {
    topicSlug
  } = useParams();
  const topic = topicBySlug(topicSlug);
  const {
    isRead,
    toggleRead
  } = useMarketProgress();
  if (!topic) return <Navigate to={MARKET_BASE_PATH} replace data-id="element-3141" data-mp="25e3z28z13c8c" />;
  const index = marketTopics.indexOf(topic);
  const prev = marketTopics[index - 1];
  const next = marketTopics[index + 1];
  const read = isRead(topic.key);
  const related = topic.related.map(topicByKey).filter(Boolean) as typeof marketTopics;
  return <article className="space-y-8" data-id="element-3142" data-mp="2t3yduw2wrav8">
      <nav aria-label="Ruta" className="flex flex-wrap items-center gap-1 text-sm text-gray-500 print:hidden" data-id="element-3143" data-mp="2t23p16oimylv">
        <Link to={MARKET_BASE_PATH} className="hover:text-white" data-id="element-3144" data-mp="2o54yey4299sf">
          Vanguard Market
        </Link>
        <ChevronRight className="h-4 w-4" aria-hidden="true" data-id="element-3145" data-mp="2j7bukbq8crvb" />
        <span className="text-gray-300" data-id="element-3146" data-mp="2f2lzgp3ebhbg">{topic.menu}</span>
      </nav>

      <header className="space-y-4" data-id="element-3147" data-mp="17pu0y52d58mi">
        <p className="text-xs font-semibold text-[#D4AF37]" data-id="element-3148" data-mp="17qv9a1rzvh0j">
          {topic.kind === 'ref' ? 'Consulta' : 'Tema para jugadores'} · {MARKET_SOURCE_LABEL}
        </p>
        <h1 className="font-cinzel text-2xl font-bold leading-tight text-white md:text-4xl" data-id="element-3149" data-mp="1554dic8galpt">{topic.title}</h1>
        <p className="max-w-3xl text-base leading-relaxed text-gray-300 md:text-lg" data-id="element-3150" data-mp="1a50ioesvwj6y">{topic.summary}</p>
        <MarketNotice data-id="element-3151" data-mp="v89i427h51fr" />
        <div className="flex flex-wrap items-center gap-2 print:hidden [&>*]:shrink-0" data-id="element-3152" data-mp="v6yb7tiqfdkf">
          <button type="button" aria-pressed={read} onClick={() => toggleRead(topic.key)} className={`inline-flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold whitespace-nowrap transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37] ${read ? 'bg-emerald-500/15 text-emerald-300' : 'border border-white/15 text-gray-200 hover:text-white'}`} data-id="element-3153" data-mp="1059l24911o2n">
            <Check className="h-4 w-4" aria-hidden="true" data-id="element-3154" data-mp="3fs957jgb5si" />
            {read ? 'Leído' : 'Marcar como leído'}
          </button>
          <CopyLinkButton path={topicPath(topic)} data-id="element-3155" data-mp="7glx20ww8kle" />
          <button type="button" onClick={printWithDetails} className="hidden h-10 items-center gap-2 md:inline-flex rounded-full border border-white/15 px-4 text-sm font-semibold text-gray-200 whitespace-nowrap transition-colors duration-150 hover:text-white" data-id="element-3156" data-mp="3kyzbziwuqdtc">
            <Printer className="h-4 w-4" aria-hidden="true" data-id="element-3157" data-mp="3u0ywwmazm21k" />
            Imprimir tema
          </button>
          <OpenMarketButton compact data-id="element-3158" data-mp="3oz9n19zi3uf2" />
        </div>
      </header>

      {topic.widget === 'calculators' && <MarketCalculators data-id="element-3159" data-mp="7qctgnyfs83w" />}

      {/* Lo esencial primero: la primera sección se lee abierta; el resto queda plegado como un índice. */}
      <div className="max-w-3xl space-y-3" data-id="element-3160" data-mp="glapw50sm2zz">
        {topic.sections.map((s, i) => i === 0 && !s.extended ? <div key={s.id} className="pb-5" data-id="element-3161" data-mp="ck8q9n2yroxh">
              <MarketSectionView section={s} data-id="element-3162" data-mp="cljs3hcummly" />
            </div> : <MarketSectionView key={s.id} section={s} collapsible data-id="element-3163" data-mp="qj8bekr9plow" />)}
      </div>

      {topic.widget && topic.widget !== 'calculators' && <MarketWidgetView widget={topic.widget} data-id="element-3164" data-mp="15mi7hebf57wk" />}

      {topic.checklist && <div className="max-w-3xl" data-id="element-3165" data-mp="19nk73w98zlz2">
          <TopicChecklist topicKey={topic.key} items={topic.checklist} data-id="element-3166" data-mp="19mil4wqp1f2k" />
        </div>}

      {related.length > 0 && <section aria-labelledby="rel-t" className="print:hidden" data-id="element-3167" data-mp="1ef28glf6nokk">
          <h2 id="rel-t" className="font-cinzel text-lg font-bold text-white" data-id="element-3168" data-mp="1jcbudr3d5i3n">
            Temas relacionados
          </h2>
          <ul className="mt-3 flex flex-wrap gap-2" data-id="element-3169" data-mp="mop64ggg161a">
            {related.map(r => <li key={r.key} data-id="element-3170" data-mp="qphgjd11xgz5">
                <Link to={topicPath(r)} className="inline-flex h-10 items-center rounded-full border border-white/15 px-4 text-sm text-gray-200 transition-colors duration-150 hover:border-[#D4AF37]/50 hover:text-white" data-id="element-3171" data-mp="qqj0k701pdt0">
                  {r.menu}
                </Link>
              </li>)}
          </ul>
        </section>}

      <nav aria-label="Tema anterior y siguiente" className="grid gap-3 border-t border-white/10 pt-6 sm:grid-cols-2 print:hidden" data-id="element-3172" data-mp="2c19bvrzr3avc">
        {prev ? <Link to={topicPath(prev)} className="group rounded-2xl border border-white/10 px-4 py-3 transition-colors duration-150 hover:border-white/25" data-id="element-3173" data-mp="gs4oqxavfr1">
            <span className="flex items-center gap-1 text-xs text-gray-500" data-id="element-3174" data-mp="3mt8gz57ulbcf">
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" data-id="element-3175" data-mp="3hvqfu102o0ez" /> Anterior
            </span>
            <span className="font-semibold text-white" data-id="element-3176" data-mp="3du4v5thaqhu8">{prev.menu}</span>
          </Link> : <span data-id="element-3177" data-mp="38tzaix9g1vln" />}
        {next && <Link to={topicPath(next)} className="group rounded-2xl border border-white/10 px-4 py-3 text-right transition-colors duration-150 hover:border-white/25" data-id="element-3178" data-mp="38t7ghqvhw5xh">
            <span className="flex items-center justify-end gap-1 text-xs text-gray-500" data-id="element-3179" data-mp="2yxe6ae590oiv">
              Siguiente <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" data-id="element-3180" data-mp="122zuc4b7khjf" />
            </span>
            <span className="font-semibold text-white" data-id="element-3181" data-mp="x4pydrhntcwr">{next.menu}</span>
          </Link>}
      </nav>
    </article>;
}